import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DollarSign, Search } from 'lucide-react'

import { workersApi, type WorkerPayScale } from '@/services/workers'
import { WorkerLifecycleNav } from '@/components/workers/worker-lifecycle-nav'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { formatCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'

type PayScaleRow = WorkerPayScale & {
  skill_role_name?: string | null
  worker_skill_id?: string | number | null
}

const formatPayTypeLabel = (row: PayScaleRow) =>
  row.pay_type_label
  || String(row.pay_type ?? '')
    .replace(/^per_/, 'Per ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())

export default function PayScalesPage() {
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const [search, setSearch] = useState('')

  const workersQuery = useQuery({
    queryKey: ['workers', 'pay-scales', serviceInstanceId],
    queryFn: () => workersApi.getWorkers({ page_size: 300, service_instance_id: serviceInstanceId ?? undefined }),
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const payScalesQuery = useQuery({
    queryKey: ['worker-pay-scales', 'list', serviceInstanceId],
    queryFn: () => workersApi.getWorkerPayScales({
      service_instance_id: serviceInstanceId ?? undefined,
      page_size: 500,
    }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const workerNameMap = useMemo(() => {
    const map = new Map<string, string>()

    for (const worker of workersQuery.data?.results ?? []) {
      const label = worker.display_name || worker.full_name || `Worker ${worker.id}`
      map.set(String(worker.id), label)
      if (worker.worker_id != null) {
        map.set(String(worker.worker_id), label)
      }
    }

    return map
  }, [workersQuery.data?.results])

  const filteredRows = useMemo(() => {
    const rows = (payScalesQuery.data ?? []) as PayScaleRow[]
    const query = search.trim().toLowerCase()
    if (!query) {
      return rows
    }

    return rows.filter((row) => {
      const workerLabel = workerNameMap.get(String(row.worker ?? '')) ?? String(row.worker ?? '')
      const skillLabel = String(row.skill_role_name ?? row.worker_skill ?? '')
      const payTypeLabel = formatPayTypeLabel(row)

      return [workerLabel, skillLabel, payTypeLabel]
        .join(' ')
        .toLowerCase()
        .includes(query)
    })
  }, [payScalesQuery.data, search, workerNameMap])

  const loading = workersQuery.isLoading || payScalesQuery.isLoading

  return (
    <div className="space-y-4 pb-2">
      <WorkerLifecycleNav activeStage="pay-scale" />

      <Card className="rounded-2xl border-border/60 bg-card shadow-sm">
        <CardHeader className="gap-3 border-b border-border/60 pb-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
              <DollarSign className="h-5 w-5" />
              PayScale
            </CardTitle>
            <div className="relative w-full md:max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="pl-9"
                placeholder="Search by worker, skill, or pay type"
              />
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Configure pay rates per worker skill before creating assignments.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-3 p-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))}
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              No pay scales found. Add worker skills first, then configure pay on a worker profile.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Worker</TableHead>
                    <TableHead>Skill</TableHead>
                    <TableHead>Pay Type</TableHead>
                    <TableHead className="text-right">Base Pay</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.map((row) => {
                    const workerId = String(row.worker ?? '')
                    const workerLabel = workerNameMap.get(workerId) ?? `Worker ${workerId}`
                    const workerHref = withServiceInstanceId(
                      `/workers/${workerId}?tab=pay_scales`,
                      serviceInstanceId,
                    )

                    return (
                      <TableRow key={String(row.id)}>
                        <TableCell className="font-medium">{workerLabel}</TableCell>
                        <TableCell>{row.skill_role_name || row.worker_skill || '-'}</TableCell>
                        <TableCell>{formatPayTypeLabel(row)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(Number(row.base_pay))}</TableCell>
                        <TableCell>
                          {row.is_active === false ? (
                            <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
                              Inactive
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                              Active
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" asChild>
                            <Link to={workerHref}>Manage</Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
